import { CircularProgress, useMediaQuery, useTheme } from "@mui/material";
import { CloseIcon, ShareViaSms } from "../../../components/Icons";
import { EmailInputWithChips, InputWithCopy } from "../../../components";
import { REFERRAL_ADVOCATE_AMOUNT, REFERRAL_REFEREE_AMOUNT } from "../../../config/params";
import React, { useEffect, useState } from "react";

import GvSecondaryButton from "../../../components/GvSecondaryButton/GvSecondaryButton";
import { MIXPANEL_EVENTS } from "../../../config/mixPanelEvents";
import { PAGES_PATH } from "../../../navigation/params";
import { StyledBookingFlowButton } from "../../containers/BookingScheduleContainer/BookingScheduleContainer.style";
import { TaskAlt } from "@mui/icons-material";
import { toastMessageActions } from "../../../state/request";
import { useCustomerReferral } from "../../../services/customer-referral/customer-referral.service";
import { useDispatch } from "react-redux";
import { useModal } from "../../../context/ModalContext";
import { useNavigate } from "react-router-dom";
import { userActivityLogger } from "../../../utils/mixpanel";

const QuickShareUpsellModal = () => {
  const { closeModal, source } = useModal();
  const navigate = useNavigate();
  const [emails, setEmails] = useState<string[]>([]);
  const [emailSent, setEmailSent] = useState(false);
  const { getReferralUrl, referralUrl, sendInvite, inviteLoading, inviteData, referralCode } = useCustomerReferral();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between(700, 1100));
  const dispatch = useDispatch();

  useEffect(() => {
    getReferralUrl();
    userActivityLogger(MIXPANEL_EVENTS.QUICK_SHARE_REFERRAL_MODAL_SHOWN, {
      source: source,
    });
  }, []);

  useEffect(() => {
    if (inviteData?.success) {
      userActivityLogger(MIXPANEL_EVENTS.SEND_EMAIL_INVITE_SUCCESS, {
        referral_code: referralCode,
        friend_emails: emails.join(","),
        no_of_emails: emails.length,
        user_session_type: "logged_in",
      });
      setEmailSent(true);
      setEmails([]);
      setTimeout(() => {
        setEmailSent(false);
      }, 4000);
    } else if (inviteData?.success === false) {
      userActivityLogger(MIXPANEL_EVENTS.SEND_EMAIL_INVITE_FAILURE, {
        referral_code: referralCode,
        friend_emails: emails.join(","),
        no_of_emails: emails.length,
        user_session_type: "logged_in",
        error_message: inviteData?.messages[0],
      });
      setEmails([]);
      dispatch(toastMessageActions.setGlobalAlert({ message: inviteData?.messages[0] || "Error Sending Invite",
        severity: "error" }));
    }
  }, [inviteData]);

  const handleSendEmails = () => {
    sendInvite(emails);
    userActivityLogger(MIXPANEL_EVENTS.SEND_EMAIL_INVITE_TRIGGERED, {
      referral_code: referralCode,
      friend_emails: emails.join(","),
      no_of_emails: emails.length,
      user_session_type: "logged_in",
    });
  };

  const handleModalClose = () => {
    closeModal();
    userActivityLogger(MIXPANEL_EVENTS.QUICK_SHARE_REFERRAL_MODAL_DISMISSED, {
      user_session_type: "logged_in",
    });
  };

  const handleLearnMore = () => {
    navigate(PAGES_PATH.REFER_FRIEND);
    userActivityLogger(MIXPANEL_EVENTS.LEARN_MORE_CLICKED, {
      user_session_type: "logged_in",
    });
    closeModal();
  };

  const handleShareViaSms = () => {
    // eslint-disable-next-line max-len
    window.open(`sms:?body=We’ve had such a great experience with GoodVets. If you’re looking for a vet, use my link for ${REFERRAL_REFEREE_AMOUNT} off your first visit. 🐾 :%20${referralUrl}`, "_self");
    userActivityLogger(MIXPANEL_EVENTS.SHARE_VIA_SMS_CLICKED, {});
  };

  return (
    <div className="relative xl:max-w-md w-full h-full bg-white rounded-2xl">
      <div className="shadow-2xl bg-transparent">
        <img src="/assets/icons/refer-friends-dog-pic.png"
          alt="invite friends image" className="rounded-t-2xl object-cover" />
      </div>
      <div className="space-y-2 w-full
      text-center max-h-[70vh] scrollbar-thin overflow-y-auto px-6 py-2
       bg-white relative bottom-1 rounded-2xl ">
        <div className="flex justify-end">
          <CloseIcon
            onClick={handleModalClose}
            className="text-black cursor-pointer fixed top-2 right-3 bg-stone-50 opacity-75
            hover:opacity-100 p-1 rounded-full"
          />
        </div>
        <div className="">
          <h3 className="text-2xl grenette-pro text-center">Invite Friends and Get {REFERRAL_ADVOCATE_AMOUNT}!</h3>
          <p className="text-sm sm:text-base text-center px-2">
           Share the care and get rewarded! Invite friends to
           GoodVets and you&apos;ll earn {REFERRAL_ADVOCATE_AMOUNT} in credits when they
           complete their first visit.
          </p>
        </div>
        <div className="text-left">
          <InputWithCopy value={referralUrl || ""} label="Copy Referral Link" referralCode={referralCode || ""} />
        </div>
        <div className="flex items-center w-full">
          <div className="flex-grow border-t-2 border-taupe-15"></div>
          <span className="mx-1 text-sm px-2">or</span>
          <div className="flex-grow border-t-2 border-taupe-15"></div>
        </div>
        {/* Invite via Email */}
        <div className="space-y-2 mb-2">
          {emailSent?
            <div>
              <label className="text-sm font-semibold block mb-1">Invite via Email</label>
              <div className="flex flex-col justify-center items-center gap-4">
                <TaskAlt sx={{ fontSize: 45 }} color="success" />
                <span className="text-sm font-semibold">Email invite sent!</span>
              </div>
            </div>:
            <div className="text-left">
              <EmailInputWithChips label="Invite via Email" emails={emails} setEmails={setEmails} />
              <StyledBookingFlowButton
                disabled={!emails.length}
                fullWidth
                color="primary"
                className="!text-sm"
                size="small"
                onClick={handleSendEmails}
              >
                {inviteLoading ? <CircularProgress color="inherit" size={20} /> : "Send Email Invite"}
              </StyledBookingFlowButton></div>}
          {/* or */}
          {(isMobile || isTablet) &&
            <>
              <div className="flex items-center w-full my-2">
                <div className="flex-grow border-t-2 border-taupe-15"></div>
                <span className="mx-1 text-sm bg-white px-2">or</span>
                <div className="flex-grow border-t-2 border-taupe-15"></div>
              </div>
              {/* Share via SMS */}
              <GvSecondaryButton
                type="button"
                onClick={handleShareViaSms}
                className="!w-full !font-semibold !border-1
                               !border-gray-4 hover:!bg-gray-3 !h-[2.5rem] !text-sm"
              >
                <span><ShareViaSms /></span>&nbsp;
                Share via SMS
              </GvSecondaryButton>
            </>}
          <p className="text-xs text-center">By referring a friend, I accept the&nbsp;
            <a href="/terms" className="underline">Terms & Conditions</a>
          </p>
          <GvSecondaryButton
            type="button"
            onClick={handleLearnMore}
            className="!w-full !font-semibold !border-1 !border-gray-4 hover:!bg-gray-3 !h-[2.5rem] !text-sm"
          >
                 Learn More
          </GvSecondaryButton>
        </div>
      </div>
    </div>
  );
};

export default QuickShareUpsellModal;
